import React from 'react';

import { DeepPartial } from '@hrlogics/helper-types';
import { FormApi } from '@hrlogics/react-components/form';

import { Nullable } from '@clearverify/types';

import { VerifierCredentialStatus } from 'src/api';
import {
  CommercialVerifierCompanyInformationFormFields,
  CommercialVerifierCompanyInformationFormValues,
  GovernmentVerifierCompanyInformationFormValues,
} from 'src/components/verifier/VerifierCompanyInformationForm/types';
import { ModalId } from 'src/constants/modals';
import { useFileUpload } from 'src/hooks/file';
import { useModal } from 'src/hooks/shared';
import { useCurrentUserPermissions } from 'src/hooks/user';

import { useVerifierById } from '../useVerifierById';

import {
  resolveCommercialVerifierUpdate,
  resolveGovernmentVerifierUpdate,
  resolveVerifierAddressUpdate,
  resolveVerifierCommonUpdate,
  resolveVerifierLogoUpdate,
  resolveVerifierStatusUpdate,
} from './utils';

export enum VerifierCompanyDataUpdateNoticeType {
  none = 'none',
  credentialStatusNotice = 'credentialStatusNotice',
  recredentialingNotice = 'recredentialingNotice',
}

type FormDataType =
  | CommercialVerifierCompanyInformationFormValues
  | GovernmentVerifierCompanyInformationFormValues;

export const useVerifierCompanyDataUpdate = <TFormData extends FormDataType>(
  verifierId: Nullable<string>,
  modalIds: Record<
    | VerifierCompanyDataUpdateNoticeType.credentialStatusNotice
    | VerifierCompanyDataUpdateNoticeType.recredentialingNotice,
    ModalId
  >,
) => {
  const { openModal } = useModal();

  const { verifier, updateVerifier } = useVerifierById(verifierId);
  const { uploadFile } = useFileUpload();

  const { hasPlatformRoles } = useCurrentUserPermissions();

  const needsRecredentialingByStatus =
    verifier && !NO_RECREDENTIALING_STATUSES.includes(verifier.credentialStatus);

  const hasFullUpdateAuthority = hasPlatformRoles;

  const hasSensitiveFieldsUpdated = React.useCallback(
    (form: FormApi<TFormData, DeepPartial<TFormData>> | FormApi<TFormData>) => {
      const dirtyKeys = Object.keys(form.getState().dirtyFields);

      return dirtyKeys.some(key => !NO_RECREDENTIALING_FIELDS.includes(key));
    },
    [],
  );

  const getIsRecredentialingNeeded = React.useCallback(
    (form: FormApi<TFormData, DeepPartial<TFormData>>) => {
      return (
        !hasFullUpdateAuthority && needsRecredentialingByStatus && hasSensitiveFieldsUpdated(form)
      );
    },
    [hasFullUpdateAuthority, hasSensitiveFieldsUpdated, needsRecredentialingByStatus],
  );

  const getVerifierUpdateNoticeType = React.useCallback(
    (form: FormApi<TFormData, DeepPartial<TFormData>> | FormApi<TFormData>) => {
      if (hasFullUpdateAuthority || !hasSensitiveFieldsUpdated(form)) {
        return VerifierCompanyDataUpdateNoticeType.none;
      }

      switch (verifier?.credentialStatus) {
        case 'Middesk_Pending':
        case 'Denied_By_Middesk':
        case 'Denied': {
          return VerifierCompanyDataUpdateNoticeType.credentialStatusNotice;
        }

        case 'Credentialed': {
          return VerifierCompanyDataUpdateNoticeType.recredentialingNotice;
        }

        default: {
          return VerifierCompanyDataUpdateNoticeType.none;
        }
      }
    },
    [hasFullUpdateAuthority, hasSensitiveFieldsUpdated, verifier?.credentialStatus],
  );

  const updateVerifierData = React.useCallback(
    async (formData: TFormData, form: FormApi<TFormData, DeepPartial<TFormData>>) => {
      const isRecredentialingNeeded = getIsRecredentialingNeeded(form);

      let newLogoId: string | undefined;
      if (formData.logoFile?.file && formData.logoFile.id !== verifier?.logo?.id) {
        const newLogo = await uploadFile(formData.logoFile.file);
        newLogoId = newLogo.id;
      }

      await updateVerifier({
        ...resolveVerifierCommonUpdate(formData),

        ...(verifier?.type === 'Commercial'
          ? resolveCommercialVerifierUpdate(
              formData as CommercialVerifierCompanyInformationFormValues,
            )
          : resolveGovernmentVerifierUpdate(
              formData as GovernmentVerifierCompanyInformationFormValues,
            )),

        ...resolveVerifierAddressUpdate(formData, verifier),

        ...resolveVerifierLogoUpdate(newLogoId),

        ...(isRecredentialingNeeded ? resolveVerifierStatusUpdate(verifier) : null),
      });
    },
    [getIsRecredentialingNeeded, updateVerifier, uploadFile, verifier],
  );

  const handleUpdate = React.useCallback(
    async (formData: TFormData, form: FormApi<TFormData, DeepPartial<TFormData>>) => {
      const noticeType = getVerifierUpdateNoticeType(form);

      if (noticeType === VerifierCompanyDataUpdateNoticeType.none) {
        await updateVerifierData(formData, form);
      } else {
        openModal(modalIds[noticeType], {
          onSubmit: async () => {
            try {
              await updateVerifierData(formData, form);
            } catch (error) {
              console.error(error);
            }
          },
        });
      }
    },
    [getVerifierUpdateNoticeType, modalIds, openModal, updateVerifierData],
  );

  return {
    verifier,
    handleUpdate,
    getIsRecredentialingNeeded,
    getVerifierUpdateNoticeType,
  };
};

const NO_RECREDENTIALING_FIELDS: string[] = [
  CommercialVerifierCompanyInformationFormFields.logoFile,
];
const NO_RECREDENTIALING_STATUSES: VerifierCredentialStatus[] = [
  VerifierCredentialStatus.NotCredentialed,
  VerifierCredentialStatus.Denied,
  VerifierCredentialStatus.DeniedByMiddesk,
  VerifierCredentialStatus.MiddeskPending,
  VerifierCredentialStatus.AwaitingApprovalByCv,
  VerifierCredentialStatus.Referred,
];
